import { useId, type ReactElement } from "react";
import type { ProblemStatementParams } from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import { cn } from "../../lib/utils.js";
import { useLimitedTextDraft } from "../text-limit.js";
import { PROBLEM_STATEMENT_MAX_LENGTH } from "./limit.js";

/**
 * Problem Statement — Specify, singleton.
 * Tinted-cyan body block with "PROBLEM" eyebrow + the problem text.
 * Ported from ui-elements/components/solves/workspace/specify/ProblemStatementCard.tsx.
 *
 * The textarea carries no `maxLength` on purpose (PB-1575): the browser
 * attribute silently truncates a paste, which reads as a successful save. Over
 * -limit text stays in the control, is not committed, and says why — see
 * `useLimitedTextDraft`.
 */
export function ProblemStatementWidget({
  data,
  onChange,
  readOnly,
  hostMetadata,
}: WidgetBodyProps<ProblemStatementParams>): ReactElement {
  const messageId = useId();
  const countId = useId();
  const draft = useLimitedTextDraft(
    data.problem,
    PROBLEM_STATEMENT_MAX_LENGTH,
    (problem) => onChange({ ...data, problem }),
  );
  const invalid = draft.message !== null;
  // Quiet until it matters: a running count under every card would be noise on
  // a paragraph nobody is near the limit of. It appears in the last tenth, and
  // turns red once the text cannot be saved.
  const showCount =
    !readOnly && draft.length > PROBLEM_STATEMENT_MAX_LENGTH * 0.9;
  // Both, in reading order, and only what is actually on screen. The count and
  // the over-limit message are the field's own description — rendered as loose
  // siblings they were visible to a sighted reader and unreachable to a screen
  // reader, so the cap and the reason for refusing a save were announced by
  // nothing. `undefined` rather than `""` when neither is present: an empty
  // attribute is still present.
  const describedBy =
    [showCount ? countId : null, invalid ? messageId : null]
      .filter((id): id is string => id !== null)
      .join(" ") || undefined;

  return (
    <div className="rounded-md bg-pb-cyan/[0.06] border border-pb-cyan/20 px-3.5 py-3 flex-1 min-h-0 flex flex-col">
      <div className="text-dense-1 uppercase tracking-wider font-semibold text-pb-cyan-text shrink-0">
        Problem
      </div>
      {hostMetadata?.title ? (
        <div className="text-sm leading-snug font-semibold text-pb-text">
          {hostMetadata.title}
        </div>
      ) : null}
      {readOnly ? (
        data.problem.trim() ? (
          <div className="mt-1.5 text-pb-text-secondary text-xs leading-relaxed">
            {data.problem}
          </div>
        ) : (
          <div className="mt-1.5 text-pb-text-muted text-xs italic">
            No problem statement yet — ask PlayerAI to draft one.
          </div>
        )
      ) : (
        <>
          <textarea
            aria-label="Problem statement"
            value={draft.value}
            onChange={(e) => draft.onChange(e.target.value)}
            onFocus={draft.onFocus}
            onBlur={draft.onBlur}
            aria-invalid={invalid ? true : undefined}
            aria-describedby={describedBy}
            placeholder="Frame the problem — what's happening, where, and what's the impact?"
            className={cn(
              "mt-1.5 flex-1 min-h-0 w-full resize-none rounded-md bg-transparent text-pb-text-secondary text-xs leading-relaxed placeholder:text-pb-text-muted/70 focus-field",
              invalid && "text-pb-red-text",
            )}
          />
          {showCount ? (
            <p
              id={countId}
              className={cn(
                "shrink-0 text-end text-dense-1 pb-tabular",
                invalid ? "text-pb-red-text" : "text-pb-text-muted",
              )}
            >
              {draft.length.toLocaleString()} /{" "}
              {PROBLEM_STATEMENT_MAX_LENGTH.toLocaleString()}
            </p>
          ) : null}
          {/* role="alert" so crossing the limit is announced while the text is
              still being typed, not only when the field is re-read. */}
          {invalid ? (
            <p
              id={messageId}
              role="alert"
              className="shrink-0 text-dense-1 text-pb-red-text"
            >
              {draft.message}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
